import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useCookies } from 'react-cookie';
import { ChevronRightIcon } from '@radix-ui/react-icons';

import { ErrorDialog } from '../../../Login/ErrorDialog';
import { LocationDialog } from '../../../Login/LocationDialog';
import { PrivatePayrollAuctionLoading } from '../Loading';
import { LoginTermsOfUse } from '../../../LoginTermsOfUse';
import { LoginTermOfAuthorization } from '../../../LoginTermOfAuthorization';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import {
    cpfMask,
    dateMask,
    formatName,
    formatToYYYYMMDD,
    isDate,
    isNaturalDocumentNumber,
} from '../../../../utils/utils';
import useInput from '../../../../utils/hooks/useInput';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField, CheckboxFieldStyles as CS } from '../components/CheckboxField';
import { ThemedInput } from '../../CreditOperationAndSocialSecurityCards/components/ThemedInput';
import * as S from './styles';

interface LoginProps {
    setSignData: (data: any) => void;
    onSendOTPCode: (token: string) => void;
    setSignerStep: (step: string) => void;
    signData: any;
    setCachedDocumentNumber: (v: string) => void;
    setCachedBirthdate: (v: string) => void;
    appTheme?: string;
}

type VisibleTerm = 'terms_of_use' | 'term_of_authorization' | null;

export const PrivatePayrollAuctionLogin = (props: LoginProps) => {
    const { clientThemeSettings } = useThemeContext();
    const {
        setSignData,
        onSendOTPCode,
        setSignerStep,
        signData,
        setCachedDocumentNumber,
        setCachedBirthdate,
    } = props;

    const { signerHash } = useParams<{ signerHash: string }>();
    const [cookies, setCookie] = useCookies([`qisign_${signerHash}`]);
    const hasMadeRequestRef = useRef(false);

    const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);
    const [termOfAuthorizationCheckboxChecked, setTermOfAuthorizationCheckboxChecked] = useState(false);
    const [termsOfUseCheckboxChecked, setTermsOfUseCheckboxChecked] = useState(false);
    const [visibleTerm, setVisibleTerm] = useState<VisibleTerm>(null);
    const [loading, setLoading] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return Boolean(params.get('document_number') || params.get('login_hash'));
    });
    const [loadingForm, setLoadingForm] = useState(false);
    const [openErrorDialog, setOpenErrorDialog] = useState('');
    const [openLocationDialog, setOpenLocationDialog] = useState(false);
    const [geolocationFailureRequestCount, setGeolocationFailureRequestCount] = useState(0);

    const documentNumberQueryParam = new URLSearchParams(window.location.search).get('document_number');
    const birthdateQueryParam = new URLSearchParams(window.location.search).get('birthdate');
    const nameQueryParam = new URLSearchParams(window.location.search).get('name');
    const loginHashQueryParam = new URLSearchParams(window.location.search).get('login_hash');

    const {
        value: documentNumberValue,
        isValid: documentNumberIsValid,
        hasError: documentNumberHasError,
        valueChangeHandler: documentNumberValueChangeHandler,
        inputBlurHandler: documentNumberInputBlurHandler,
        reset: documentNumberReset,
    } = useInput(isNaturalDocumentNumber);

    const {
        value: birthdateValue,
        isValid: birthdateIsValid,
        hasError: birthdateHasError,
        valueChangeHandler: birthdateValueChangeHandler,
        inputBlurHandler: birthdateInputBlurHandler,
        reset: birthdateReset,
    } = useInput(isDate);

    const resetQueryParams = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('document_number');
        url.searchParams.delete('birthdate');
        url.searchParams.delete('name');
        url.searchParams.delete('login_hash');
        window.history.replaceState({}, document.title, url.toString());
    };

    const collectGeoLocation = useCallback(async () => {
        if (navigator.geolocation) {
            try {
                const position: any = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject);
                });
                return {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                };
            } catch (error) {
                setGeolocationFailureRequestCount((c) => c + 1);
                return { latitude: null, longitude: null };
            }
        }
        return { latitude: null, longitude: null };
    }, []);

    const handleSendOTPCode = useCallback(
        async (responseData: any) => {
            if (
                responseData.use_authentication_code &&
                !responseData.skip_initial_notification &&
                !cookies[`qisign_${signerHash}`]
            ) {
                onSendOTPCode(responseData.signer_token);
            }
        },
        [onSendOTPCode, signerHash, cookies],
    );

    const isValidInputData = useCallback(
        (checkboxChecked?: boolean) => {
            const effectiveCheckbox = checkboxChecked !== undefined ? checkboxChecked : termsCheckboxChecked;

            if (cookies[`qisign_${signerHash}`]) {
                setOpenErrorDialog('');
                return true;
            }

            if (!effectiveCheckbox && !documentNumberQueryParam) {
                setOpenErrorDialog('terms_not_checked');
                return false;
            }

            if (signData?.signature_settings?.document_number_required) {
                if (!documentNumberIsValid || !birthdateIsValid) {
                    setOpenErrorDialog('invalid_data');
                    return false;
                }
            }

            setOpenErrorDialog('');
            return true;
        },
        [
            birthdateIsValid,
            documentNumberIsValid,
            termsCheckboxChecked,
            documentNumberQueryParam,
            signData,
            cookies,
            signerHash,
        ],
    );

    const onSubmit = useCallback(
        async (
            documentNumber: string,
            birthdate: string,
            name: string | null,
            alreadyFormatted: boolean,
            latitude: number | null,
            longitude: number | null,
            browserKey: string | null,
        ) => {
            const payload: any = {};

            if (documentNumber) {
                payload['document_number'] = alreadyFormatted ? documentNumber : cpfMask(documentNumber);
            }
            if (browserKey) {
                payload['device_data'] = { device_key: browserKey };
            }
            if (birthdate) {
                payload['birthdate'] = alreadyFormatted ? birthdate : formatToYYYYMMDD(birthdate);
            }
            if (name !== null && name !== '') {
                payload['name'] = formatName(name);
            }
            if (latitude && longitude) {
                payload['gps_data'] = { coordinates: [longitude, latitude] };
            }

            axios
                .post('/signer/hash/' + signerHash, payload)
                .then(async (response) => {
                    const session_id = uuidv4();
                    setSignData({ ...signData, ...response.data, session_id });
                    await handleSendOTPCode(response.data);
                    setOpenLocationDialog(false);
                    documentNumberReset();
                    birthdateReset();
                    setCookie(`qisign_${signerHash}`, payload, {
                        path: `/s/${signerHash}`,
                        maxAge: 60 * 60,
                    });
                    setSignerStep(response.data.next_step);
                    setCachedDocumentNumber(payload.document_number);
                    setCachedBirthdate(payload.birthdate);
                })
                .catch((error) => {
                    if ([401, 403, 409].includes((error.response || {}).status)) {
                        setOpenLocationDialog(false);
                        setOpenErrorDialog('incorrect_personal_data');
                    }
                    setCookie(`qisign_${signerHash}`, '', { path: `/s/${signerHash}`, maxAge: 0 });
                    setLoading(false);
                })
                .finally(() => {
                    setLoadingForm(false);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setCookie, setSignData, handleSendOTPCode, birthdateReset, documentNumberReset, signerHash, signData, setSignerStep],
    );

    const executeSubmission = useCallback(
        async (
            docNum?: string,
            bdate?: string,
            name?: string,
            alreadyFormatted = false,
        ) => {
            let browserKey: string | null = null;
            try {
                browserKey = await getOrCreateBrowserKey();
            } catch (_) {}

            let latitude: number | null = null;
            let longitude: number | null = null;

            if (docNum) {
                setLoading(true);
                if (
                    signData?.signature_settings &&
                    !signData.signature_settings?.prevent_geo_location_request &&
                    signData.signature_settings?.geo_location_required
                ) {
                    const geo = await collectGeoLocation();
                    latitude = geo.latitude;
                    longitude = geo.longitude;
                    if (!latitude && !longitude) {
                        setLoading(false);
                        setOpenLocationDialog(true);
                        return;
                    }
                }
                onSubmit(docNum, bdate ?? '', name ?? null, alreadyFormatted, latitude, longitude, browserKey);
            } else {
                if (!isValidInputData()) return;
                setLoadingForm(true);
                if (
                    !signData?.signature_settings?.prevent_geo_location_request &&
                    signData?.signature_settings?.geo_location_required
                ) {
                    const geo = await collectGeoLocation();
                    latitude = geo.latitude;
                    longitude = geo.longitude;
                    if (!latitude && !longitude) {
                        setLoadingForm(false);
                        setOpenLocationDialog(true);
                        return;
                    }
                }
                onSubmit(documentNumberValue, birthdateValue, null, alreadyFormatted, latitude, longitude, browserKey);
            }
        },
        [signData, documentNumberValue, birthdateValue, isValidInputData, collectGeoLocation, onSubmit],
    );

    useEffect(() => {
        if (hasMadeRequestRef.current) return;
        hasMadeRequestRef.current = true;

        if (loginHashQueryParam) {
            if (!signData.signer_data?.document_number) return;
            const handleHashFlow = async () => {
                const keyToInherit = signData.device_data?.device_key ?? null;
                try {
                    await getOrCreateBrowserKey(keyToInherit);
                } catch (_) {}
                executeSubmission(
                    signData.signer_data.document_number,
                    signData.signer_data.birthdate,
                    signData.signer_data.name,
                    true,
                );
                resetQueryParams();
            };
            handleHashFlow();
            return;
        }

        if (documentNumberQueryParam) {
            executeSubmission(documentNumberQueryParam, birthdateQueryParam ?? undefined, nameQueryParam ?? undefined, false);
            resetQueryParams();
            return;
        }

        if (
            cookies[`qisign_${signerHash}`] &&
            isNaturalDocumentNumber(cookies[`qisign_${signerHash}`].document_number)
        ) {
            executeSubmission(
                cookies[`qisign_${signerHash}`].document_number,
                cookies[`qisign_${signerHash}`].birthdate,
                undefined,
                true,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLocationDialogClose = () => {
        setOpenLocationDialog(false);
        setLoadingForm(false);
        setLoading(false);
    };

    const primary = resolveColor('primary', clientThemeSettings);
    const text = resolveColor('text', clientThemeSettings);
    const isTermsOfUseRequired = signData?.signature_settings?.login_terms?.terms_of_use?.enabled;
    const isTermOfAuthorizationRequired = signData?.signature_settings?.login_terms?.term_of_authorization?.enabled;

    const allFieldsValid = documentNumberIsValid && birthdateIsValid;
    const allCheckboxesChecked =
        termsCheckboxChecked &&
        (!isTermsOfUseRequired || termsOfUseCheckboxChecked) &&
        (!isTermOfAuthorizationRequired || termOfAuthorizationCheckboxChecked);

    const isSubmitDisabled = !allFieldsValid || !allCheckboxesChecked || loadingForm;

    const handleSetVisibleTerm = (term: string) => setVisibleTerm(term as VisibleTerm);

    if (visibleTerm === 'terms_of_use') {
        return <LoginTermsOfUse setVisibleTerm={handleSetVisibleTerm} />;
    }
    if (visibleTerm === 'term_of_authorization') {
        return (
            <LoginTermOfAuthorization
                setVisibleTerm={handleSetVisibleTerm}
                type={signData?.signature_settings?.login_terms?.term_of_authorization?.type}
            />
        );
    }
    if (loading) {
        return <PrivatePayrollAuctionLoading />;
    }

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.FormContainer>
                <S.IntroSection>
                    <S.Title $color={primary}>
                        {signData?.signature_settings?.login_content?.title ?? 'Bem-vindo'}
                    </S.Title>
                    <S.Subtitle>
                        {signData?.signature_settings?.login_content?.text
                            ?? 'Para iniciar o processo de contratação de empréstimo, preencha os campos abaixo:'}
                    </S.Subtitle>
                </S.IntroSection>

                <S.FormFieldsWrapper
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') executeSubmission();
                    }}
                >
                    <ThemedInput
                        labelValue="Número do CPF"
                        labelColor="#666"
                        onChange={documentNumberValueChangeHandler}
                        onBlur={documentNumberInputBlurHandler}
                        hasError={documentNumberHasError}
                        fieldValue={cpfMask(documentNumberValue)}
                        errorMessage="Número de CPF inválido."
                        type="numeric"
                    />
                    <ThemedInput
                        labelValue="Data de Nascimento (DD/MM/AAAA)"
                        labelColor="#666"
                        onChange={birthdateValueChangeHandler}
                        onBlur={birthdateInputBlurHandler}
                        hasError={birthdateHasError}
                        fieldValue={dateMask(birthdateValue)}
                        errorMessage="Data de nascimento inválida."
                        type="numeric"
                    />
                </S.FormFieldsWrapper>

                <S.CheckboxGroup>
                    <CheckboxField
                        id="qisign-terms"
                        checked={termsCheckboxChecked}
                        onChange={(e) => setTermsCheckboxChecked(e.target.checked)}
                        checkboxColor={primary}
                        textColor={text}
                    >
                        Concordo com os{' '}
                        <a href="https://sign.qitech.com.br/docs/termos_de_uso.pdf" target="_blank" rel="noreferrer" style={{ color: primary }}>
                            Termos de Uso
                        </a>{' '}
                        e{' '}
                        <a href="https://sign.qitech.com.br/docs/politica_de_privacidade.pdf" target="_blank" rel="noreferrer" style={{ color: primary }}>
                            Política de Privacidade
                        </a>
                        .
                    </CheckboxField>

                    {isTermsOfUseRequired && (
                        <CheckboxField
                            id="qisign-terms-of-use"
                            checked={termsOfUseCheckboxChecked}
                            onChange={(e) => setTermsOfUseCheckboxChecked(e.target.checked)}
                            checkboxColor={primary}
                            textColor={text}
                        >
                            Concordo com o{' '}
                            <CS.PseudoLink $color={primary} onClick={() => setVisibleTerm('terms_of_use')}>
                                Termo de Uso
                            </CS.PseudoLink>
                            {' '}da QI SCD.
                        </CheckboxField>
                    )}

                    {isTermOfAuthorizationRequired && (
                        <CheckboxField
                            id="qisign-term-of-authorization"
                            checked={termOfAuthorizationCheckboxChecked}
                            onChange={(e) => setTermOfAuthorizationCheckboxChecked(e.target.checked)}
                            checkboxColor={primary}
                            textColor={text}
                        >
                            Autorizo o{' '}
                            <CS.PseudoLink $color={primary} onClick={() => setVisibleTerm('term_of_authorization')}>
                                compartilhamento de dados com Dataprev
                            </CS.PseudoLink>
                            .
                        </CheckboxField>
                    )}
                </S.CheckboxGroup>

                <S.SubmitButton
                    $backgroundColor={primary}
                    $disabled={isSubmitDisabled}
                    disabled={isSubmitDisabled}
                    onClick={() => { if (!isSubmitDisabled) executeSubmission(); }}
                >
                    {loadingForm ? 'Carregando...' : 'Iniciar'}
                    <ChevronRightIcon width={24} height={24} />
                </S.SubmitButton>
            </S.FormContainer>

            <S.Footer>
                <PoweredByQITech />
            </S.Footer>

            {openLocationDialog && (
                <LocationDialog
                    open={openLocationDialog}
                    onClose={handleLocationDialogClose}
                    onSubmit={handleLocationDialogClose}
                    geolocationFailureRequestCount={geolocationFailureRequestCount}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
            {openErrorDialog && (
                <ErrorDialog
                    description={openErrorDialog}
                    onClose={() => setOpenErrorDialog('')}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
        </S.PageWrapper>
    );
};
